import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { getSetupProgress, updateSetupProgress, getSchoolSettings, setGradingSystem } from "../lib/api";

const STEPS = [
  {
    key: "branding",
    title: "School Branding",
    description: "Upload your logo, set school name, motto, and color palette.",
    href: "/settings?tab=branding",
    icon: "🎨",
  },
  {
    key: "calendar",
    title: "Academic Calendar",
    description: "Set school year dates, semester boundaries, holidays, and half-days.",
    href: "/calendar",
    icon: "📅",
  },
  {
    key: "blocks",
    title: "Block Schedule & Bell Times",
    description: "Define your block structure (Block 1, 2…) and bell times.",
    href: "/settings?tab=block-schedule",
    icon: "🕐",
  },
  {
    key: "classes",
    title: "Classes & Groups",
    description: "Set up morning/afternoon class groups and assign students to them.",
    href: "/settings?tab=class-groups",
    icon: "🏫",
  },
  {
    key: "groups",
    title: "Advisory Groups",
    description: "Create advisory groups and configure how advisories are organized.",
    href: "/settings?tab=advisory",
    icon: "👥",
  },
  {
    key: "staff",
    title: "Staff Files & Permissions",
    description: "Upload your staff spreadsheet (or add manually), then assign positions and permissions.",
    href: "/staff-files",
    icon: "🧑\u200d🏫",
  },
  {
    key: "students",
    title: "Students & Parent Contacts",
    description: "Bulk import students from Excel or add manually. Parent contacts are part of each student record.",
    href: "/students",
    icon: "🎒",
  },
  {
    key: "sending_schools",
    title: "Sending Schools & Contacts",
    description: "Add the schools/districts that place students with you (upload the Sending Schools Excel), and upload your contacts. Found under Contacts.",
    href: "/contacts?tab=sending-schools",
    icon: "🏫",
  },
  {
    key: "proficiency",
    title: "Proficiency Indicators",
    description: "Review the default 93 indicators — keep, customize, or add your own.",
    href: "/settings?tab=proficiency",
    icon: "📈",
  },
  {
    key: "assessments",
    title: "Assessment Settings",
    description: "Confirm the assessment categories (engagement, participation, etc.) match your school's rubric.",
    href: "/settings?tab=self-assess",
    icon: "📝",
  },
  {
    key: "risk_assessment",
    title: "Risk Assessment Bands",
    description: "Set the score cutoffs for the Low / Moderate / Elevated / High risk bands.",
    href: "/settings?tab=risk",
    icon: "⚠️",
  },
  {
    key: "communication_types",
    title: "Communication Types",
    description: "Customize the categories used in the Communication Log (phone, email, meeting, etc.).",
    href: "/settings?tab=communication-types",
    icon: "💬",
  },
  {
    key: "signups",
    title: "Sign-Ups & Trips Templates",
    description: "Create reusable sign-ups, permission slips, and field-trip events for parents.",
    href: "/advisory?tab=signups",
    icon: "📋",
  },
  {
    key: "permissions",
    title: "Permissions",
    description: "Review which positions can access which features (Admin / Teacher / Clinician / Special Ed).",
    href: "/permissions",
    icon: "🔐",
  },
  {
    key: "notifications",
    title: "Notifications & Email",
    description: "Review email setup, the Monday recap recipients, and alert preferences.",
    href: "/settings?tab=notifications",
    icon: "✉️",
  },
];

function StepRow({ step, status, onComplete, onSkip, onUnset }) {
  const navigate = useNavigate();
  const statusBadge =
    status === "completed" ? <Badge className="bg-green-600">✓ Done</Badge> :
    status === "skipped" ? <Badge variant="outline" className="border-amber-500 text-amber-700">Skipped</Badge> :
    <Badge variant="outline">To do</Badge>;
  return (
    <div className="border rounded-lg p-3 flex items-start gap-3 hover:bg-muted/20 transition" data-testid={`wizard-step-${step.key}`}>
      <div className="text-2xl pt-0.5">{step.icon}</div>
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold text-sm">{step.title}</h4>
          {statusBadge}
        </div>
        <p className="text-xs text-muted-foreground">{step.description}</p>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <Button size="sm" onClick={() => navigate(step.href)} className="bg-[#00305E] hover:bg-[#00305E]/90 h-7 text-xs" data-testid={`wizard-configure-${step.key}`}>
          Configure
        </Button>
        {status !== "completed" ? (
          <Button size="sm" variant="ghost" onClick={() => onComplete(step.key)} className="h-6 text-xs" data-testid={`wizard-mark-done-${step.key}`}>
            Mark done
          </Button>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => onUnset(step.key)} className="h-6 text-xs" data-testid={`wizard-undo-${step.key}`}>
            Undo
          </Button>
        )}
        {status !== "skipped" && status !== "completed" && (
          <Button size="sm" variant="ghost" onClick={() => onSkip(step.key)} className="h-6 text-xs text-muted-foreground" data-testid={`wizard-skip-${step.key}`}>
            Skip
          </Button>
        )}
      </div>
    </div>
  );
}

// ============ Grading System selection (first setup step, locked once chosen) ============
const GRADING_OPTIONS = [
  {
    key: "proficiency",
    title: "Proficiency-Based Grading",
    icon: "📈",
    blurb: "Track long-term proficiency indicators & transferable skills (Achieved / In Progress / Not Yet). The current SMARTIS grading model.",
  },
  {
    key: "traditional",
    title: "Traditional Grading",
    icon: "🅰️",
    blurb: "Letter grades A, B, C, D, F with percentage scores, one grade per subject each semester.",
  },
];

export function GradingSystemStep() {
  const [settings, setSettings] = useState(null);
  const [choice, setChoice] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await getSchoolSettings();
      setSettings(res.data);
      setChoice(res.data.grading_system || null);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const locked = settings?.grading_locked;
  const current = settings?.grading_system;

  const handleConfirm = async () => {
    if (!choice) return;
    setSaving(true);
    try {
      await setGradingSystem(choice);
      toast.success("Grading system saved and locked");
      setConfirmOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save grading system");
    } finally {
      setSaving(false);
    }
  };

  if (settings === null) {
    return <Card><CardContent className="py-4 text-sm text-muted-foreground">Loading grading options…</CardContent></Card>;
  }

  return (
    <Card className="border-[#00305E]/40" data-testid="grading-system-step">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">Grading System</CardTitle>
          {locked ? (
            <Badge className="bg-[#00305E]" data-testid="grading-locked-badge">🔒 Locked</Badge>
          ) : (
            <Badge variant="outline" className="border-amber-500 text-amber-700">Choose to continue</Badge>
          )}
        </div>
        <CardDescription>
          {locked
            ? "Your school's grading system is set. It can only be changed by resetting the school (Settings → Data Management → Reset & Start a Real School)."
            : "Pick how your school records academic grades. This choice is permanent — it locks after you confirm and can only be changed with a full school reset."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          {GRADING_OPTIONS.map((opt) => {
            const selected = choice === opt.key;
            const isCurrent = current === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                disabled={locked}
                onClick={() => !locked && setChoice(opt.key)}
                className={`text-left rounded-lg border p-3 transition ${
                  selected ? "border-[#00305E] ring-2 ring-[#00305E]/30 bg-[#00305E]/5" : "border-muted hover:border-[#00305E]/50"
                } ${locked && !isCurrent ? "opacity-40" : ""} ${locked ? "cursor-default" : "cursor-pointer"}`}
                data-testid={`grading-option-${opt.key}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{opt.icon}</span>
                  <span className="font-semibold text-sm">{opt.title}</span>
                  {isCurrent && locked && <Badge className="bg-green-600 ml-auto">Selected</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">{opt.blurb}</p>
              </button>
            );
          })}
        </div>
        {!locked && (
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={!choice}
            className="bg-[#00305E] hover:bg-[#00305E]/90"
            data-testid="grading-confirm-lock-btn"
          >
            Confirm & Lock Grading System
          </Button>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="grading-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Lock in {GRADING_OPTIONS.find((o) => o.key === choice)?.title}?</DialogTitle>
            <DialogDescription>
              This permanently sets your school's grading system. You will <strong>not</strong> be able to change it later
              anywhere in the app — the only way to switch is to reset the entire school and start over. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="grading-confirm-cancel">Cancel</Button>
            <Button onClick={handleConfirm} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="grading-confirm-yes">
              {saving ? "Saving…" : "Confirm & Lock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


export function SetupWizardContent({ onClose }) {
  const [progress, setProgress] = useState({ completed_steps: [], skipped_steps: [], dismissed: false });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await getSetupProgress();
      setProgress(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load setup progress");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const update = async (payload) => {
    try {
      const res = await updateSetupProgress(payload);
      setProgress((p) => ({ ...p, ...res.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const completed = new Set(progress.completed_steps || []);
  const skipped = new Set(progress.skipped_steps || []);
  const total = STEPS.length;
  const doneCount = completed.size;
  const pct = Math.round(100 * doneCount / total);

  const statusOf = (key) => completed.has(key) ? "completed" : skipped.has(key) ? "skipped" : "todo";

  const handleComplete = (key) => update({ step: key });
  const handleSkip = (key) => update({ skip_step: key });
  const handleUnset = (key) => update({ unset_step: key });
  const handleDismiss = async () => { await update({ dismissed: true }); onClose?.(); };

  if (loading) return <Card><CardContent className="py-8 text-center">Loading…</CardContent></Card>;

  return (
    <Card data-testid="setup-wizard">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle>Welcome to SMARTIS — Set-Up Wizard</CardTitle>
            <CardDescription>
              Step through these to configure SMARTIS for your school. Every step is optional — skip anything that doesn't apply yet, you can come back anytime from Settings.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-2xl font-bold text-[#00305E]">{doneCount}/{total}</div>
              <div className="text-xs text-muted-foreground">{pct}% complete</div>
            </div>
            {onClose && (
              <Button variant="outline" onClick={handleDismiss} data-testid="wizard-dismiss">
                Dismiss
              </Button>
            )}
          </div>
        </div>
        <div className="w-full bg-muted rounded-full h-2 mt-3">
          <div className="bg-[#00305E] h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <GradingSystemStep />
        {STEPS.map((step) => (
          <StepRow
            key={step.key}
            step={step}
            status={statusOf(step.key)}
            onComplete={handleComplete}
            onSkip={handleSkip}
            onUnset={handleUnset}
          />
        ))}
        {doneCount === total && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg mt-4">
            <div className="font-bold text-green-700">🎉 All set!</div>
            <p className="text-sm">You've completed every setup step. Welcome aboard.</p>
            <Button asChild className="mt-2 bg-[#00305E] hover:bg-[#00305E]/90">
              <Link to="/">Go to Dashboard</Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Auto-popup wizard shown on Dashboard for empty schools that haven't dismissed. */
export default function SetupWizardAutoPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getSetupProgress();
        // Show only for empty schools that haven't dismissed
        if (res.data.is_empty_school && !res.data.dismissed) {
          setOpen(true);
        }
      } catch { /* silent */ }
    })();
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="setup-wizard-dialog">
        <SetupWizardContent onClose={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}
